import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import type { RootState } from '../index';

// Peer matching types
export interface PeerProfile {
  id: string;
  userId: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  bio?: string;
  skillLevel: number; // 0-1
  masteryAreas: string[];
  learningGoals: string[];
  preferredLearningStyle: string;
  timeZone: string;
  availableHours: string[];
  joinedAt: string;
  lastActive: string;
  isOnline: boolean;
}

export interface CompatibilityScore {
  overall: number; // 0-1
  skillAlignment: number;
  goalAlignment: number;
  styleAlignment: number;
  scheduleAlignment: number;
  personalityAlignment: number;
  breakdown: CompatibilityBreakdown;
}

export interface CompatibilityBreakdown {
  skillLevelDifference: number;
  commonMasteryAreas: string[];
  sharedGoals: string[];
  learningStyleMatch: boolean;
  timeZoneCompatibility: number;
  scheduleOverlap: number;
  communicationStyleMatch: number;
}

export interface PeerMatch {
  id: string;
  peer: PeerProfile;
  compatibility: CompatibilityScore;
  aiReasoning: string;
  confidence: number;
  matchedAt: string;
  interactionHistory: PeerInteraction[];
  connectionStatus: 'suggested' | 'pending' | 'connected' | 'declined';
  lastInteraction?: string;
}

export interface PeerInteraction {
  id: string;
  type: 'message' | 'study_session' | 'challenge' | 'collaboration';
  timestamp: string;
  duration?: number;
  rating?: number;
  feedback?: string;
}

export interface ConnectionRequest {
  id: string;
  fromUserId: string;
  toUserId: string;
  message: string;
  aiGeneratedIntro: boolean;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  createdAt: string;
  respondedAt?: string;
  expiresAt: string;
}

export interface PeerMatchingAnalytics {
  totalMatches: number;
  successfulConnections: number;
  averageCompatibilityScore: number;
  connectionSuccessRate: number;
  averageResponseTime: number;
  topMatchingFactors: string[];
  userEngagementScore: number;
  lastUpdated: string;
}

export interface PeerMatchingPreferences {
  userId: string;
  skillLevelRange: [number, number];
  preferredMasteryAreas: string[];
  learningGoalAlignment: number; // 0-1, how important goal alignment is
  scheduleFlexibility: number; // 0-1, how flexible with timing
  communicationStyle: 'formal' | 'casual' | 'mixed';
  groupSizePreference: 'one_on_one' | 'small_group' | 'large_group' | 'any';
  languagePreferences: string[];
  timeZoneFlexibility: number; // hours difference acceptable
  activityTypes: string[];
  autoAcceptHighMatches: boolean;
  autoAcceptThreshold: number;
}

interface PeerMatchingState {
  matches: PeerMatch[];
  connectionRequests: {
    sent: ConnectionRequest[];
    received: ConnectionRequest[];
  };
  preferences: PeerMatchingPreferences | null;
  analytics: PeerMatchingAnalytics | null;
  loading: {
    matches: boolean;
    connections: boolean;
    preferences: boolean;
    analytics: boolean;
  };
  error: string | null;
  lastUpdated: string | null;
  matchingInProgress: boolean;
  nextMatchingScheduled: string | null;
}

const initialState: PeerMatchingState = {
  matches: [],
  connectionRequests: {
    sent: [],
    received: [],
  },
  preferences: null,
  analytics: null,
  loading: {
    matches: false,
    connections: false,
    preferences: false,
    analytics: false,
  },
  error: null,
  lastUpdated: null,
  matchingInProgress: false,
  nextMatchingScheduled: null,
};

// Async thunks
export const fetchPeerMatches = createAsyncThunk(
  'peerMatching/fetchMatches',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual GraphQL query
      const response = await fetch(`/api/ai/peer-matching/${userId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch peer matches: ${response.statusText}`);
      }
      const matches: PeerMatch[] = await response.json();
      return matches;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch peer matches');
    }
  }
);

export const generatePeerMatches = createAsyncThunk(
  'peerMatching/generateMatches',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual AI matching service call
      const response = await fetch(`/api/ai/peer-matching/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      if (!response.ok) {
        throw new Error(`Failed to generate matches: ${response.statusText}`);
      }
      const matches: PeerMatch[] = await response.json();
      return matches;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to generate peer matches'
      );
    }
  }
);

export const sendConnectionRequest = createAsyncThunk(
  'peerMatching/sendConnectionRequest',
  async (
    request: {
      toUserId: string;
      message?: string;
      useAIIntro?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/peer-connections/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to send connection request: ${response.statusText}`
        );
      }
      const connectionRequest: ConnectionRequest = await response.json();
      return connectionRequest;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to send connection request'
      );
    }
  }
);

export const respondToConnectionRequest = createAsyncThunk(
  'peerMatching/respondToConnectionRequest',
  async (
    response: {
      requestId: string;
      action: 'accept' | 'decline';
      message?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const apiResponse = await fetch('/api/peer-connections/respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(response),
      });
      if (!apiResponse.ok) {
        throw new Error(
          `Failed to respond to connection request: ${apiResponse.statusText}`
        );
      }
      const updatedRequest: ConnectionRequest = await apiResponse.json();
      return updatedRequest;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to respond to connection request'
      );
    }
  }
);

export const updateMatchingPreferences = createAsyncThunk(
  'peerMatching/updatePreferences',
  async (
    preferences: Partial<PeerMatchingPreferences>,
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/peer-matching/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences),
      });
      if (!response.ok) {
        throw new Error(`Failed to update preferences: ${response.statusText}`);
      }
      const updatedPreferences: PeerMatchingPreferences = await response.json();
      return updatedPreferences;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to update matching preferences'
      );
    }
  }
);

export const fetchMatchingAnalytics = createAsyncThunk(
  'peerMatching/fetchAnalytics',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(`/api/peer-matching/analytics/${userId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch analytics: ${response.statusText}`);
      }
      const analytics: PeerMatchingAnalytics = await response.json();
      return analytics;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch matching analytics'
      );
    }
  }
);

const peerMatchingSlice = createSlice({
  name: 'peerMatching',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearMatches: state => {
      state.matches = [];
      state.lastUpdated = null;
    },
    updateMatchStatus: (
      state,
      action: PayloadAction<{
        matchId: string;
        status: PeerMatch['connectionStatus'];
      }>
    ) => {
      const match = state.matches.find(m => m.id === action.payload.matchId);
      if (match) {
        match.connectionStatus = action.payload.status;
        match.lastInteraction = new Date().toISOString();
      }
    },
    addPeerInteraction: (
      state,
      action: PayloadAction<{ matchId: string; interaction: PeerInteraction }>
    ) => {
      const match = state.matches.find(m => m.id === action.payload.matchId);
      if (match) {
        match.interactionHistory.push(action.payload.interaction);
        match.lastInteraction = action.payload.interaction.timestamp;
      }
    },
    setMatchingInProgress: (state, action: PayloadAction<boolean>) => {
      state.matchingInProgress = action.payload;
    },
    scheduleNextMatching: (state, action: PayloadAction<string>) => {
      state.nextMatchingScheduled = action.payload;
    },
    optimisticUpdatePreferences: (
      state,
      action: PayloadAction<Partial<PeerMatchingPreferences>>
    ) => {
      if (state.preferences) {
        state.preferences = {
          ...state.preferences,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: builder => {
    // Fetch Peer Matches
    builder
      .addCase(fetchPeerMatches.pending, state => {
        state.loading.matches = true;
        state.error = null;
      })
      .addCase(fetchPeerMatches.fulfilled, (state, action) => {
        state.loading.matches = false;
        state.matches = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchPeerMatches.rejected, (state, action) => {
        state.loading.matches = false;
        state.error = action.payload as string;
      });

    // Generate Peer Matches
    builder
      .addCase(generatePeerMatches.pending, state => {
        state.matchingInProgress = true;
        state.error = null;
      })
      .addCase(generatePeerMatches.fulfilled, (state, action) => {
        state.matchingInProgress = false;
        state.matches = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(generatePeerMatches.rejected, (state, action) => {
        state.matchingInProgress = false;
        state.error = action.payload as string;
      });

    // Send Connection Request
    builder
      .addCase(sendConnectionRequest.pending, state => {
        state.loading.connections = true;
        state.error = null;
      })
      .addCase(sendConnectionRequest.fulfilled, (state, action) => {
        state.loading.connections = false;
        state.connectionRequests.sent.push(action.payload);

        // Update match status
        const match = state.matches.find(
          m => m.peer.userId === action.payload.toUserId
        );
        if (match) {
          match.connectionStatus = 'pending';
          match.lastInteraction = action.payload.createdAt;
        }
      })
      .addCase(sendConnectionRequest.rejected, (state, action) => {
        state.loading.connections = false;
        state.error = action.payload as string;
      });

    // Respond to Connection Request
    builder
      .addCase(respondToConnectionRequest.pending, state => {
        state.loading.connections = true;
        state.error = null;
      })
      .addCase(respondToConnectionRequest.fulfilled, (state, action) => {
        state.loading.connections = false;

        // Update the request in received list
        const requestIndex = state.connectionRequests.received.findIndex(
          r => r.id === action.payload.id
        );
        if (requestIndex >= 0) {
          state.connectionRequests.received[requestIndex] = action.payload;
        }

        // Update match status if applicable
        const match = state.matches.find(
          m => m.peer.userId === action.payload.fromUserId
        );
        if (match) {
          match.connectionStatus =
            action.payload.status === 'accepted' ? 'connected' : 'declined';
          match.lastInteraction =
            action.payload.respondedAt || new Date().toISOString();
        }
      })
      .addCase(respondToConnectionRequest.rejected, (state, action) => {
        state.loading.connections = false;
        state.error = action.payload as string;
      });

    // Update Matching Preferences
    builder
      .addCase(updateMatchingPreferences.pending, state => {
        state.loading.preferences = true;
        state.error = null;
      })
      .addCase(updateMatchingPreferences.fulfilled, (state, action) => {
        state.loading.preferences = false;
        state.preferences = action.payload;
      })
      .addCase(updateMatchingPreferences.rejected, (state, action) => {
        state.loading.preferences = false;
        state.error = action.payload as string;
      });

    // Fetch Matching Analytics
    builder
      .addCase(fetchMatchingAnalytics.pending, state => {
        state.loading.analytics = true;
        state.error = null;
      })
      .addCase(fetchMatchingAnalytics.fulfilled, (state, action) => {
        state.loading.analytics = false;
        state.analytics = action.payload;
      })
      .addCase(fetchMatchingAnalytics.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearMatches,
  updateMatchStatus,
  addPeerInteraction,
  setMatchingInProgress,
  scheduleNextMatching,
  optimisticUpdatePreferences,
} = peerMatchingSlice.actions;

// Selectors
export const selectPeerMatches = (state: RootState) =>
  state.peerMatching.matches;
export const selectConnectionRequests = (state: RootState) =>
  state.peerMatching.connectionRequests;
export const selectMatchingPreferences = (state: RootState) =>
  state.peerMatching.preferences;
export const selectMatchingAnalytics = (state: RootState) =>
  state.peerMatching.analytics;
export const selectPeerMatchingLoading = (state: RootState) =>
  state.peerMatching.loading;
export const selectPeerMatchingError = (state: RootState) =>
  state.peerMatching.error;
export const selectMatchingInProgress = (state: RootState) =>
  state.peerMatching.matchingInProgress;

// Complex selectors
export const selectHighCompatibilityMatches = createSelector(
  [selectPeerMatches],
  matches => matches.filter(match => match.compatibility.overall >= 0.8)
);

export const selectPendingConnections = createSelector(
  [selectConnectionRequests],
  requests => requests.received.filter(req => req.status === 'pending')
);

export const selectConnectedPeers = createSelector(
  [selectPeerMatches],
  matches => matches.filter(match => match.connectionStatus === 'connected')
);

export const selectMatchesByCompatibility = createSelector(
  [selectPeerMatches],
  matches =>
    [...matches].sort(
      (a, b) => b.compatibility.overall - a.compatibility.overall
    )
);

export const selectRecentInteractions = createSelector(
  [selectPeerMatches],
  matches => {
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return matches
      .flatMap(match =>
        match.interactionHistory
          .filter(interaction => new Date(interaction.timestamp) > oneWeekAgo)
          .map(interaction => ({
            ...interaction,
            peerName: match.peer.displayName,
          }))
      )
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
  }
);

export default peerMatchingSlice.reducer;
